import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Avatar,
  Chip,
  CircularProgress,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
} from '@mui/material';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import { format } from 'date-fns';
import { formatDate as formatDateUtil } from '../../utils/formatters';
import { useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import styles from './Profile.module.scss';
import {
  useGetCurrentUserQuery,
  useUpdateUserMutation,
} from '../../store/api/userApi';
import { useToast } from '../../components/common/Toast/ToastProvider';
import PhoneInput from '../../components/common/PhoneInput/PhoneInput';
import { useGetPhoneCountryCodesQuery } from '../../store/api/phoneCodeApi';

const ProfilePage: React.FC = () => {
  const [tabValue, setTabValue] = useState('1');
  const [isEditing, setIsEditing] = useState(false);

  // -- ADDED: state for bio editing
  const [bioDraft, setBioDraft] = useState('');

  // position removed from formData
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    phone_number: '',
    address: '',
    employee_id: '',
    date_of_birth: '',
  });

  const {
    data: userData,
    isLoading,
    error,
  } = useGetCurrentUserQuery();
  const [updateUser, { isLoading: isSaving }] = useUpdateUserMutation();
  const { showToast } = useToast();

  const { control, setValue, watch, trigger } = useForm({
    defaultValues: { contact_phone: '' }
  });

  const watchedPhone = watch('contact_phone');

  const { data: phoneCountryCodes = [], isLoading: isPhoneCodesLoading, error: phoneCodesError } =
    useGetPhoneCountryCodesQuery();

  useEffect(() => {
    if (error) {
      showToast('Failed to load profile data', 'error');
    }
  }, [error, showToast]);

  useEffect(() => {
    if (watchedPhone !== undefined && watchedPhone !== formData.phone_number) {
      setFormData(prev => ({
        ...prev,
        phone_number: watchedPhone
      }));
    }
  }, [watchedPhone]);

  const location = useLocation();

  useEffect(() => {
    setIsEditing(false);
  }, [location.key]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: string) => {
    setTabValue(newValue);
  };

  const handleEditToggle = async () => {
    if (isEditing) {

      const isPhoneValid = await trigger("contact_phone");

      if (!isPhoneValid) {
        return; // stop save if invalid
      }
      // Save: PATCH user
      try {
        await updateUser({
          userId: 'me',
          userData: {
            first_name: formData.first_name,
            last_name: formData.last_name,
            phone_number: formData.phone_number,
            employee_id: formData.employee_id,
            additional_info: {
              profile_picture: userData?.additional_info?.profile_picture || null,
              address: formData.address,
              date_of_birth: formData.date_of_birth || null,
              // --- ADD THE BIO
              bio: bioDraft,
            },
          },
        }).unwrap();
        showToast('Profile updated successfully!', 'success');
        setIsEditing(false);
      } catch (err: any) {
        showToast('Failed to update profile', 'error');
      }
    } else {
      // Start editing: set form fields with current user data
      if (userData) {
        setFormData({
          first_name: userData.first_name,
          last_name: userData.last_name || '',
          phone_number: userData.phone_number || '',
          address: userData.additional_info?.address || '',
          employee_id: userData.employee_id || '',
          date_of_birth: userData.additional_info?.date_of_birth || '',
        });
        setValue('contact_phone', userData.phone_number || '');
        setBioDraft(userData.additional_info?.bio || '');
      }
      setIsEditing(true);
    }
  };

  const handleCancel = () => {
    // Reset form data and exit edit mode without saving
    setIsEditing(false);
    setFormData({
      first_name: '',
      last_name: '',
      phone_number: '',
      address: '',
      employee_id: '',
      date_of_birth: '',
    });
    setBioDraft('');
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // -- handle bio input
  const handleBioChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setBioDraft(e.target.value);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '--';
    return formatDateUtil(dateString, 'MMM d, yyyy h:mm a', dateString);
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <div className="appWrapper">
        <h3 className="pageTitle">Profile</h3>
        <div>Failed to load profile data</div>
      </div>
    );
  }

  if (!userData) {
    return (
      <div className="appWrapper">
        <h3 className="pageTitle">Profile</h3>
        <div>No user data available</div>
      </div>
    );
  }

  return (
    <div className="appWrapper">
      <h3 className="pageTitle">My Profile</h3>
      <div className="appGridContainer">
        <div className="gridHeader">
          <div className="gridHeadRight">
            <ul className="iconsList">
              <li>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleEditToggle}
                  className="button button__solid--primary"
                  disabled={isSaving}
                >
                  {isSaving
                    ? 'Saving...'
                    : isEditing
                      ? 'Save Changes'
                      : 'Edit Profile'}
                </Button>
              </li>
              {isEditing && (
                <li>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={handleCancel}
                    className="button button__outlined--secondary"
                    disabled={isSaving}
                  >
                    Cancel
                  </Button>
                </li>
              )}
            </ul>
          </div>
        </div>

        <div className="gridWrapper" style={{ padding: '20px' }}>
          <div className={styles.userDetWrapper}>
            <div
              className={`${styles.userDetContainer} bordered r10`}
              style={{ padding: '24px' }}
            >
              <Box display="flex" alignItems="center" sx={{ mb: 3 }}>
                <Avatar
                  sx={{ width: 80, height: 80, mr: 3 }}
                  alt={`${userData.first_name} ${userData.last_name}`}
                >
                  {`${userData.first_name.charAt(0)}${userData.last_name?.charAt(0) || ''}`}
                </Avatar>
                <Box>
                  {isEditing ? (
                    <Box
                      sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 2,
                        mb: 1,
                        alignItems: 'flex-start',
                      }}
                    >
                      <TextField
                        name="first_name"
                        value={formData.first_name}
                        onChange={handleInputChange}
                        label="First Name"
                        variant="outlined"
                        sx={{ minWidth: 220, flex: 1 }}
                        size="small"
                      />
                      <TextField
                        name="last_name"
                        value={formData.last_name}
                        onChange={handleInputChange}
                        label="Last Name"
                        variant="outlined"
                        sx={{ minWidth: 220, flex: 1 }}
                        size="small"
                      />
                    </Box>
                  ) : (
                    <>
                      <Typography variant="h4" gutterBottom>
                        {userData.first_name} {userData.last_name}
                      </Typography>
                      <Typography variant="subtitle1" color="text.secondary">
                        {userData.email}
                      </Typography>
                    </>
                  )}
                </Box>
              </Box>

              {/* BIO SECTION */}
              <section className="pb_20 pt_10">
                <Card variant="outlined" sx={{ background: '#fafbfc' }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Bio
                    </Typography>
                    {isEditing ? (
                      <TextField
                        value={bioDraft}
                        onChange={handleBioChange}
                        multiline
                        minRows={3}
                        maxRows={6}
                        fullWidth
                        placeholder="Write something about yourself..."
                        variant="outlined"
                        sx={{ mb: 1 }}
                      />
                    ) : (
                      <Typography
                        variant="body1"
                        sx={{
                          minHeight: 24,
                          color: userData.additional_info?.bio
                            ? 'text.primary'
                            : 'text.disabled',
                        }}
                      >
                        {userData.additional_info?.bio || 'No bio provided.'}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </section>

              {/* BASIC INFO SECTION 1 */}
              <section className={`border_bottom ${styles.userBasic}`}>
                <Box
                  display="flex"
                  flexWrap="wrap"
                  gap={2}
                  sx={{ mt: 2, mb: 2 }}
                >
                  <Box flex={1} minWidth={200}>
                    <label className="label">Status</label>
                    <h5 className="body1">
                      <Chip
                        label={userData.is_active ? 'Active' : 'Inactive'}
                        color={userData.is_active ? 'success' : 'error'}
                        size="small"
                      />
                    </h5>
                  </Box>
                  <Box flex={1} minWidth={200}>
                    <label className="label">Employee ID</label>
                    {isEditing ? (
                      <TextField
                        name="employee_id"
                        value={formData.employee_id}
                        onChange={handleInputChange}
                        variant="outlined"
                        fullWidth
                        size="small"
                        sx={{ mt: 0.5 }}
                      />
                    ) : (
                      <h5 className="body1">{userData.employee_id || '--'}</h5>
                    )}
                  </Box>
                  <Box flex={1} minWidth={200}>
                    <label className="label">Contact Number</label>
                    {isEditing ? (
                      <PhoneInput
                        control={control}
                        name="contact_phone"
                        countryCodes={phoneCountryCodes.map((country) => ({
                          code: country.dial_code,
                          flag: country.region,
                        }))}
                        isLoading={isPhoneCodesLoading}
                        hasError={!!phoneCodesError}
                      />
                    ) : (
                      <h5 className="body1">{userData.phone_number || '--'}</h5>
                    )}
                  </Box>
                </Box>
              </section>

              {/* BASIC INFO SECTION 2 */}
              <section className={`border_bottom ${styles.userBasic}`}>
                <Box
                  display="flex"
                  flexWrap="wrap"
                  gap={2}
                  sx={{ mt: 2, mb: 2 }}
                >
                  <Box flex={1} minWidth={200}>
                    <label className="label">Position</label>
                    {/* Always render as static text - never editable */}
                    <h5 className="body1">{userData.position || '--'}</h5>
                  </Box>
                  <Box flex={1} minWidth={200}>
                    <label className="label">Department</label>
                    <h5 className="body1">
                      {userData.department_details?.name ||
                        userData.department ||
                        '--'}
                    </h5>
                  </Box>
                  <Box flex={1} minWidth={200}>
                    <label className="label">Company</label>
                    <h5 className="body1">
                      {userData.company_details?.name || '--'}
                    </h5>
                  </Box>
                </Box>
              </section>

              {/* ADDRESS & DOB FIELDS */}
              <section className="border_bottom pb_20 pt_20">
                <Box
                  display="flex"
                  flexWrap="wrap"
                  gap={2}
                  sx={{ mt: 2, mb: 2 }}
                >
                  <Box flex={2} minWidth={300}>
                    <label className="label">Address</label>
                    {isEditing ? (
                      <TextField
                        name="address"
                        value={formData.address}
                        onChange={handleInputChange}
                        variant="outlined"
                        fullWidth
                        size="small"
                        sx={{ mt: 0.5 }}
                      />
                    ) : (
                      <h5 className="body1">
                        {userData.additional_info?.address || '--'}
                      </h5>
                    )}
                  </Box>
                  <Box flex={1} minWidth={200}>
                    <label className="label">Date of Birth</label>
                    {isEditing ? (
                      <TextField
                        name="date_of_birth"
                        type="date"
                        value={formData.date_of_birth || ''}
                        onChange={handleInputChange}
                        variant="outlined"
                        fullWidth
                        size="small"
                        sx={{ mt: 0.5 }}
                        InputLabelProps={{ shrink: true }}
                      />
                    ) : (
                      <h5 className="body1 mt-2">
                        {userData.additional_info?.date_of_birth
                          ? format(
                              new Date(userData.additional_info.date_of_birth),
                              'yyyy-MM-dd'
                            )
                          : '--'}
                      </h5>
                    )}
                  </Box>
                </Box>
              </section>

              {/* INVITATION */}
              <section className="border_bottom pb_30 pt_30">
                <Box
                  display="flex"
                  flexWrap="wrap"
                  gap={2}
                  sx={{ mt: 1, mb: 1 }}
                >
                  <Box flex={1} minWidth={180}>
                    <label className="label">Invitation Sent</label>
                    <h5 className="body1">
                      {formatDate(userData.invitation_sent_at)}
                    </h5>
                  </Box>
                  <Box flex={1} minWidth={180}>
                    <label className="label">Invitation Accepted</label>
                    <h5 className="body1">
                      {formatDate(userData.invitation_accepted_at)}
                    </h5>
                  </Box>
                </Box>
              </section>
            </div>

            {/* TABS */}
            <section className={`tab_container mt_30`}>
              <TabContext value={tabValue}>
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                  <TabList onChange={handleTabChange} aria-label="profile tabs">
                    <Tab label="Roles" value="1" />
                    <Tab label="Permissions" value="2" />
                  </TabList>
                </Box>

                <TabPanel value="1">
                  {userData?.roles && userData?.roles?.length > 0 ? (
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>Role Name</TableCell>
                            <TableCell>Description</TableCell>
                            <TableCell>Type</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {userData?.roles.map((role: any) => (
                            <TableRow key={role.id}>
                              <TableCell>
                                <Chip
                                  label={role.name}
                                  color={
                                    role.name.toLowerCase() === 'superadmin'
                                      ? 'secondary'
                                      : role.name.toLowerCase() === 'admin'
                                        ? 'primary'
                                        : 'default'
                                  }
                                />
                              </TableCell>
                              <TableCell>{role.description || '-'}</TableCell>
                              <TableCell>
                                {role.is_system_role
                                  ? 'System Role'
                                  : 'Custom Role'}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <div className="empty_state">
                      <p>No roles assigned</p>
                    </div>
                  )}
                </TabPanel>

                <TabPanel value="2">
                  {userData?.permissions && userData.permissions?.length > 0 ? (
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>Permission Name</TableCell>
                            <TableCell>Description</TableCell>
                            <TableCell>Role</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {userData.permissions.map((permission: any) => (
                            <TableRow key={permission.permission_id}>
                              <TableCell>
                                {permission.permission_name}
                              </TableCell>
                              <TableCell>
                                {permission.permission_description || '-'}
                              </TableCell>
                              <TableCell>{permission.role_name}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <div className="empty_state">
                      <p>No permissions assigned</p>
                    </div>
                  )}
                </TabPanel>
              </TabContext>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
